import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import Chip from '@mui/material/Chip';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { ResizableLayout } from '#components/resizable-layout/resizable-layout.js';
import { AddFindingButtons } from './components/add-finding-buttons.js';
import { FindingsDetailsPane } from './components/findings-details-pane.js';
import { FindingsTable } from './components/findings-table.js';
import { useGetFindings } from './hooks/use-get-findings.js';

export const Route = createFileRoute(
	'/_authorization-gate/orgs/$orgName/projects/$projectId/revisions/$revisionId/ai-findings',
)({
	validateSearch: z.object({
		findingId: z.string().optional(),
		requirementResultId: z.string().optional(),
	}),
	staticData: {
		authorize: (ability) => ability.can('manage', 'legacyFinding'),
		navigation: {
			label: 'AI Findings',
			keywords: ['findings', 'issues', 'problems', 'violations', 'ai'],
		},
	},
	loader: async ({ context: { queryClient, getProjectPreviewOptions } }) => {
		const { projectPreview } = await queryClient.ensureQueryData(getProjectPreviewOptions);
		return { projectPreview };
	},
	head: ({ loaderData }) => ({
		meta: [{ title: (loaderData ? loaderData.projectPreview.name : 'Project') + ' • AI Findings • Cadstrom' }],
	}),
	component: ReportGenerationPage,
});

function ReportGenerationPage() {
	const navigate = Route.useNavigate();
	const { projectId, revisionId } = Route.useParams();
	const { findingId, requirementResultId } = Route.useSearch();
	const { findings, fetching } = useGetFindings({
		projectId,
		buildId: revisionId,
		...(requirementResultId ? { requirementResultId } : {}),
	});

	const handleClearFilter = () => {
		void navigate({
			search: (prev) => {
				const { requirementResultId: _, ...rest } = prev;
				return rest;
			},
		});
	};

	return (
		<div className='flex h-screen w-full flex-col gap-4 p-3 pt-2'>
			<div className='flex flex-col gap-2'>
				<h1 className='text-xl font-semibold'>AI findings</h1>
				<h2 className='font-semibold text-gray-a11'>Project {projectId}</h2>
			</div>

			{requirementResultId !== undefined && (
				<div className='flex items-center gap-2'>
					<FilterListIcon className='text-blue-a10' />
					<span className='text-sm text-gray-a11'>Filtered by requirement result:</span>
					<Chip
						label={requirementResultId}
						size='small'
						onDelete={handleClearFilter}
						deleteIcon={<CloseIcon />}
						color='primary'
						variant='outlined'
					/>
				</div>
			)}

			<div className='min-h-0 flex-1'>
				<ResizableLayout split='vertical' initialProportions={['1fr', '1fr']} className='h-full'>
					<div className='flex h-full min-h-0 flex-col overflow-hidden'>
						<div className='mb-4 flex justify-start'>
							<AddFindingButtons projectId={projectId} buildId={revisionId} />
						</div>
						<FindingsTable findings={findings} selectedFindingId={findingId} loading={fetching} />
					</div>
					<FindingsDetailsPane findings={findings} selectedFindingId={findingId} />
				</ResizableLayout>
			</div>
		</div>
	);
}
